/**
 * Revenue-by-family chart (WSRADM-0038 / WSRADM-0040 / WSRADM-0042). Always-
 * stacked family bars (or a single Total bar when dimension is null). Never
 * sums families; tooltip prints buckets[].revenue as sent. Legend items toggle
 * families (WSRADM-0051): a hidden family is not drawn and not listed in the
 * tooltip, and the tooltip Total stays the server's revenue.
 */
import { useId } from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { BarPlot } from '@mui/x-charts/BarChart';
import { LinePlot } from '@mui/x-charts/LineChart';
import { ChartsAxisHighlight } from '@mui/x-charts/ChartsAxisHighlight';
import { ChartsClipPath } from '@mui/x-charts/ChartsClipPath';
import { ChartsDataProvider } from '@mui/x-charts/ChartsDataProvider';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { ChartsSurface } from '@mui/x-charts/ChartsSurface';
import {
  ChartsTooltipContainer,
  useAxesTooltip,
} from '@mui/x-charts/ChartsTooltip';
import { ChartsWrapper } from '@mui/x-charts/ChartsWrapper';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { useTranslation } from 'react-i18next';
import { formatMoney, formatMoneyCompact } from '@/api/reports/format';
import type {
  FamilyColumn,
  RevenueOverlapProduct,
  SeriesBucket,
  SeriesResponse,
} from '@/api/reports/types';
import { familyViewKey } from '@/config/viewPrefs';
import { useCopy } from '@/hooks/useCopy';
import { ChartLegend } from './ChartLegend';
import { DASHBOARD_CHART_MARGIN } from './chartLayout';
import { periodTickLabel, periodTooltipLabel } from './periodLabels';
import { dailyReferenceMarkers } from './rangeModel';
import {
  NEUTRAL_FAMILY_COLOR,
  resolveFamilyColumnColors,
  revenueSeries,
} from './seriesModel';

export interface RevenueChartProps {
  response: SeriesResponse;
  granularity: 'day' | 'month';
  width?: number | undefined;
  height?: number | undefined;
  skipAnimation?: boolean | undefined;
  /** Hidden family keys (`family:<slug>`); hidden families are not drawn. */
  hidden?: ReadonlySet<string> | undefined;
  onToggle?: ((key: string) => void) | undefined;
  onShowAll?: (() => void) | undefined;
  yoySeries?: (number | null)[] | undefined;
  yoyPeriods?: (string | null)[] | undefined;
  yoyUnavailable?: boolean | undefined;
}

const NONE_HIDDEN: ReadonlySet<string> = new Set();
const noop = () => {};

/** Y-axis money formatter — exported so tests exercise the same path as the chart. */
export function revenueYAxisValueFormatter(
  value: number | null,
  currency: string,
  locale: string,
): string {
  return value === null ? '' : formatMoneyCompact(value, currency, locale);
}

/** Product line for the overlap section — renders fields as sent. */
export function formatOverlapProductLine(
  product: RevenueOverlapProduct,
  familyColumns: readonly FamilyColumn[],
  linesPhrase: string,
): string {
  const identity =
    product.productName !== null
      ? `${product.productName} (#${product.sourceProductId})`
      : String(product.sourceProductId);
  const families = product.familySlugs
    .map((slug) => familyColumns.find((c) => c.slug === slug)?.displayName ?? slug)
    .join(', ');
  return `${identity} — ${families} — ${linesPhrase}`;
}

export interface RevenueBucketTooltipContentProps {
  bucket: SeriesBucket;
  familyColumns: readonly FamilyColumn[];
  /** Same palette/tenant colours as the stacked bars, aligned with familyColumns. */
  familyColors: readonly string[];
  currency: string;
  locale: string;
  periodLabel: string;
  totalLabel: string;
  /** Hidden family keys — those families are not listed (Total is unchanged). */
  hidden?: ReadonlySet<string> | undefined;
  yoy?: { periodLabel: string; revenue: number | null } | null | undefined;
}

/** Presentational tooltip body — also unit-tested without chart hover. */
export function RevenueBucketTooltipContent({
  bucket,
  familyColumns,
  familyColors,
  currency,
  locale,
  periodLabel,
  totalLabel,
  hidden = NONE_HIDDEN,
  yoy,
}: RevenueBucketTooltipContentProps) {
  const { t } = useCopy();
  const money = (amount: number) => formatMoney(amount, currency, locale);
  const hiddenFamilies = familyColumns.filter((column) =>
    hidden.has(familyViewKey(column.slug)),
  ).length;

  return (
    <Box
      data-testid="revenue-tooltip"
      sx={{
        p: 1.5,
        maxWidth: 360,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 2,
      }}
    >
      <Typography variant="subtitle2" gutterBottom>
        {periodLabel}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {totalLabel}: {money(bucket.revenue)}
      </Typography>
      {yoy !== undefined && yoy !== null ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('dashboard.yoy.tooltip', 'Last year ({{period}})', { period: yoy.periodLabel })}:{' '}
          {yoy.revenue !== null ? money(yoy.revenue) : '—'}
        </Typography>
      ) : null}
      {familyColumns.map((column, index) => {
        if (hidden.has(familyViewKey(column.slug))) return null;
        const cell = bucket.families.find((f) => f.slug === column.slug);
        const amount = cell?.revenue ?? 0;
        const chipBg = familyColors[index] ?? NEUTRAL_FAMILY_COLOR;
        const isUnassigned = column.slug === null;
        return (
          <Box
            key={column.slug ?? '__unmapped__'}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}
          >
            <Box
              component="span"
              aria-hidden
              data-testid={`revenue-tooltip-chip-${column.slug ?? 'unassigned'}`}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                flexShrink: 0,
                bgcolor: chipBg,
                border: isUnassigned ? '1px solid' : undefined,
                borderColor: isUnassigned ? 'divider' : undefined,
              }}
            />
            <Typography variant="body2">
              {column.displayName}: {money(amount)}
            </Typography>
          </Box>
        );
      })}
      {hiddenFamilies > 0 ? (
        <Typography variant="caption" color="text.secondary" component="div">
          {t('dashboard.revenue.hiddenInTooltip', '{{count}} hidden families not listed', {
            count: hiddenFamilies,
          })}
        </Typography>
      ) : null}
      {bucket.overlapLineCount > 0 ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              'reports.revenue.overlapLines',
              '{{n}} lines counted in more than one family',
              { n: bucket.overlapLineCount },
            )}
          </Typography>
          {bucket.overlapProducts.map((product) => {
            const linesPhrase = t('reports.revenue.overlapLineCountOf', '{{n}} lines', {
              n: product.lineCount,
            });
            return (
              <Typography
                key={product.sourceProductId}
                variant="caption"
                component="div"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {formatOverlapProductLine(product, familyColumns, linesPhrase)}
              </Typography>
            );
          })}
          {bucket.overlapProductsTruncated > 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }} component="div">
              {t('reports.revenue.overlapMore', '+{{n}} more products', {
                n: bucket.overlapProductsTruncated,
              })}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}

function RevenueAxisTooltip({
  buckets,
  familyColumns,
  familyColors,
  currency,
  locale,
  totalLabel,
  periodFormatter,
  hidden,
  yoySeries,
  yoyPeriods,
  daily,
}: {
  buckets: readonly SeriesBucket[];
  familyColumns: readonly FamilyColumn[];
  familyColors: readonly string[];
  currency: string;
  locale: string;
  totalLabel: string;
  periodFormatter: (period: string) => string;
  hidden: ReadonlySet<string>;
  yoySeries?: (number | null)[] | undefined;
  yoyPeriods?: (string | null)[] | undefined;
  daily: boolean;
}) {
  const tooltips = useAxesTooltip();
  if (tooltips === null || tooltips.length === 0) return null;
  const first = tooltips[0]!;
  const bucket = buckets[first.dataIndex];
  if (!bucket) return null;
  const yoyVal = yoySeries ? yoySeries[first.dataIndex] ?? null : null;
  const yoyPeriod = yoyPeriods ? yoyPeriods[first.dataIndex] : null;
  const yoyPeriodLabel = yoyPeriod ? periodTooltipLabel(yoyPeriod, daily, locale) : periodFormatter(bucket.period);

  return (
    <RevenueBucketTooltipContent
      bucket={bucket}
      familyColumns={familyColumns}
      familyColors={familyColors}
      currency={currency}
      locale={locale}
      periodLabel={periodFormatter(bucket.period)}
      totalLabel={totalLabel}
      hidden={hidden}
      yoy={yoySeries ? { periodLabel: yoyPeriodLabel, revenue: yoyVal } : undefined}
    />
  );
}

export function RevenueChart({
  response,
  granularity,
  width,
  height = 280,
  skipAnimation = false,
  hidden = NONE_HIDDEN,
  onToggle = noop,
  onShowAll = noop,
  yoySeries,
  yoyPeriods,
  yoyUnavailable = false,
}: RevenueChartProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const theme = useTheme();
  const clipPathId = `${useId()}-clip-path`;
  const daily = granularity === 'day';
  const totalLabel = t('dashboard.revenue.total', 'Total');
  const spec = revenueSeries(response, theme.palette.text.primary, totalLabel, hidden);
  const familyColors = resolveFamilyColumnColors(response.familyColumns);
  const hideLegend = response.dimension === null;
  const anyOverlap =
    response.dimension !== null && spec.overlapBuckets.some(Boolean);
  const hasYoy = Boolean(yoySeries && yoySeries.length > 0);

  const monthLabel = (period: string): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(period);
    if (!match) return '';
    return new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(
      new Date(Number(match[1]), Number(match[2]) - 1, 1),
    );
  };

  const moneyFormatter = (v: number | null) =>
    v === null ? '' : formatMoney(v, response.currency, i18n.language);

  const series = [
    ...(response.dimension !== null
      ? spec.families.map((s) => ({
          type: 'bar' as const,
          id: s.key,
          data: s.data,
          label: s.label,
          color: s.color,
          stack: s.stack,
          valueFormatter: moneyFormatter,
        }))
      : [
          {
            type: 'bar' as const,
            id: spec.total.key,
            data: spec.total.data,
            label: spec.total.label,
            color: spec.total.color,
            valueFormatter: moneyFormatter,
          },
        ]),
    ...(hasYoy && yoySeries
      ? [
          {
            type: 'line' as const,
            id: 'yoy-revenue',
            data: yoySeries,
            label: t('dashboard.yoy.legend', 'Last year'),
            color: theme.palette.grey[500],
            showMark: false,
            valueFormatter: moneyFormatter,
          },
        ]
      : []),
  ];

  const captionParts = [response.currency];
  if (response.dimension !== null) captionParts.push(response.dimension.displayName);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, '& .MuiLineChart-line': { strokeDasharray: '4 4' } }}
      data-testid="revenue-chart"
    >
      <Typography variant="subtitle1" sx={{ mb: 0.5 }} component="h2">
        {t('dashboard.revenue.title', 'Revenue by family')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {captionParts.join(' · ')}
      </Typography>
      <ChartsDataProvider
        {...(width !== undefined ? { width } : {})}
        height={height}
        margin={DASHBOARD_CHART_MARGIN}
        series={series}
        xAxis={[
          {
            id: 'revenue-x',
            scaleType: 'band',
            data: spec.periods,
            valueFormatter: (value: string | null) =>
              value === null ? '' : periodTickLabel(value, daily, i18n.language),
          },
        ]}
        yAxis={[
          {
            id: 'revenue-y',
            valueFormatter: (v: number | null) =>
              revenueYAxisValueFormatter(v, response.currency, i18n.language),
          },
        ]}
      >
        <ChartsWrapper hideLegend>
          <ChartsSurface>
            <ChartsGrid horizontal />
            <g clipPath={`url(#${clipPathId})`}>
              <BarPlot skipAnimation={skipAnimation} />
              {hasYoy ? <LinePlot skipAnimation={skipAnimation} /> : null}
              <ChartsAxisHighlight x="band" />
            </g>
            <ChartsXAxis axisId="revenue-x" />
            <ChartsYAxis axisId="revenue-y" />
            <ChartsClipPath id={clipPathId} />
            {daily
              ? dailyReferenceMarkers(spec.periods).map((marker) =>
                  marker.type === 'sunday' ? (
                    <ChartsReferenceLine
                      key={`sun-${marker.period}`}
                      x={marker.period}
                      lineStyle={{ strokeDasharray: '4 4', stroke: theme.palette.divider }}
                    />
                  ) : (
                    <ChartsReferenceLine
                      key={`mom-${marker.period}`}
                      x={marker.period}
                      label={monthLabel(marker.period)}
                      labelAlign="start"
                      lineStyle={{ stroke: theme.palette.divider }}
                    />
                  ),
                )
              : null}
          </ChartsSurface>
          <ChartsTooltipContainer trigger="axis">
            <RevenueAxisTooltip
              buckets={response.buckets}
              familyColumns={response.familyColumns}
              familyColors={familyColors}
              currency={response.currency}
              locale={i18n.language}
              totalLabel={totalLabel}
              periodFormatter={(period) =>
                periodTooltipLabel(period, daily, i18n.language)
              }
              hidden={hidden}
              yoySeries={yoySeries}
              yoyPeriods={yoyPeriods}
              daily={daily}
            />
          </ChartsTooltipContainer>
        </ChartsWrapper>
      </ChartsDataProvider>
      {yoyUnavailable ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
          data-testid="yoy-unavailable-caption"
        >
          {t(
            'dashboard.yoy.unavailable',
            'Last year’s figures aren’t available for this range.',
          )}
        </Typography>
      ) : null}
      {!hideLegend ? (
        <ChartLegend
          items={spec.legend}
          onToggle={onToggle}
          onShowAll={onShowAll}
          testId="revenue-legend"
        />
      ) : null}
      {anyOverlap ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
          data-testid="revenue-overlap-caption"
        >
          {t(
            'dashboard.revenue.overlapCaption',
            "Where a product is in more than one family it is counted in each, so the stack can exceed the month's total — hover for details.",
          )}
        </Typography>
      ) : null}
    </Paper>
  );
}
